import React, { Fragment, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Tooltip } from '@mui/material';
import '../../Styles/MultiFileSelect.css'
import { IoCloseCircleOutline } from 'react-icons/io5';
import { BiSolidFilePdf } from "react-icons/bi";
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE_MB = 5; // Maximum file size allowed (5MB)

const MultiFileSelect = ({ 
    className,
    name = '',
    label,
    errors,
    required,
    register,
    defaultValue,
    onValueChange
}) => {
    const [files, setFiles] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const { t } = useTranslation();

    const { getRootProps, getInputProps, open } = useDropzone({
        accept: { "application/pdf": [] },
        noClick: true,
        noKeyboard: true,
        onDropAccepted: (acceptedFiles) => handleFiles(acceptedFiles),
        onDropRejected: (rejectedFiles) => handleRejectedFiles(rejectedFiles),
    });

    useEffect(() => {
        if (defaultValue?.length > 0) {
            setFiles(defaultValue?.map((mapDt) => transformToFileObject(mapDt)));
        } else {
            setFiles([])
        }
    }, [defaultValue]);

    const transformToFileObject = (apiResponse) => {
        return new File(
            [Uint8Array.from(atob(apiResponse.file), c => c.charCodeAt(0))],
            apiResponse.fileName,
            {
                type: apiResponse.contentType,
                lastModified: Date.now(),
            }
        );
    };

    const handleFiles = (newFiles) => {
        let updatedFiles = [...files];
        let errors = [];
    
        newFiles.forEach((file) => {    
          // ✅ Check if file size exceeds limit
          if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            errors.push(`File "${file.name}" exceeds the 5MB limit.`);
            return;
          }
    
          // ✅ Check if the file is already uploaded (Duplicate check)
          const isDuplicate = files.some(
            (existingFile) =>
              existingFile.name === file.name &&
              existingFile.size === file.size &&
              existingFile.type === file.type
          );
    
          if (isDuplicate) {
            errors.push(`File "${file.name}" is already uploaded.`);
            return;
          }
    
          updatedFiles.push(file);
        });
    
        // ✅ Show error messages if any
        if (errors.length > 0) {
          setErrorMessage(errors.join(" "));
        } else {
          setErrorMessage(""); // Clear error messages when valid files are added
          setFiles(updatedFiles);
          onValueChange(updatedFiles, name);
        }
    };

    const handleRejectedFiles = (rejectedFiles) => {
        const errorMessages = rejectedFiles.map((file) => {
          if (!file.type || file.type !== "application/pdf") {
            return `Only PDF files are allowed.`;
          }
          return null;
        });
    
        setErrorMessage(errorMessages.filter(Boolean).join(" "));
    };

    const handleFileRemove = (index) => {
          const updatedFiles = files.filter((_, i) => i !== index); // ✅ new array
    onValueChange(updatedFiles, name);
        setFiles(updatedFiles);
    };

    return (
        <Fragment>
            <div style={{ height: '100%' }} className={`${className}`}>
                <div className='h-[15%] mb-2'>
                    {label && (
                        <label style={{ height: '10%', marginBottom: '0.3rem' }} className='grc-label block'>
                            {label}
                            {required && <span className='text-danger'>*</span>}
                        </label>
                    )}
                </div>
                <div style={{ 
                        width: '100%', 
                        padding: '0px 10px', 
                        height: '85%', 
                        borderRadius: '10px', 
                        border: '2px solid hsl(var(--border))', 
                        display: 'flex', 
                        flexDirection: 'row', 
                        justifyContent: 'space-between', 
                        alignItems: 'center' 
                    }}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100px', alignItems: 'center' }}>
                        <div {...getRootProps({ className: 'dropzone flex-grow flex flex-col justify-center' })}>
                            <input {...register(name, { 
          required: { value: required, message: "File is required" } 
        })}{...getInputProps()} />
                            <div style={{ 
                                    width: '100%', 
                                    display: 'flex', 
                                    flexDirection: 'row', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center' 
                                }}>
                                <Button 
                                    type="button" 
                                    onClick={open}
                                    variant='outlined'
                                    component='label'
                                    className='!text-subMenu-selected !font-bold'
                                    htmlFor='file-upload-input'
                                    sx={{ 
                                        margin: '5px', 
                                        width: '110px', 
                                        whiteSpace: 'nowrap', 
                                        height: '80%', 
                                        color: 'hsl(var(--primary))', 
                                        border: '2px solid hsl(var(--primary))',
                                        fontSize: '0.85rem !important',
                                    }}
                                >
                                    {t('Choose File')}
                                </Button>
                                <span className='text-[11.5px]'>Or drop file(s) here</span>
                            </div>
                        </div>
                        <div style={{ 
                                width: '100%', 
                                marginLeft: '0.2rem', 
                                display: 'flex', 
                                flexDirection: 'row', 
                                textAlign: 'center' 
                            }}>
                            <span className='text-[10px] text-[hsl(var(--grey))]'>File Supported: PDF</span>
                        </div>
                    </div>
                    <span className="divider" />
                    <div style={{ width: '60%', height: '100px' }}>
                        {files?.length > 0 ? (
                            <List style={{ 
                                    width: '100%', 
                                    height: '80%',
                                    overflow: 'auto', 
                                    padding: '0px' 
                                }}>
                                {files?.map((file, index) => (
                                    <ListItem sx={{ width: '100%', height: '100%', padding: '0px' }} key={index}>
                                        <IconButton onClick={() => handleFileRemove(index)}>
                                            <BiSolidFilePdf style={{ fontSize: '20px', color: 'hsl(var(--destructive))' }} />
                                        </IconButton>
                                        <ListItemText primary={<p className="file-name">{file.name}</p>} />
                                        <ListItemSecondaryAction sx={{ width: '10%' }}>
                                            <Tooltip 
                                                title="Delete File" 
                                                placement="top" 
                                                componentsProps={{ 
                                                    tooltip: { 
                                                        sx: { 
                                                            bgcolor: 'hsl(var(--card))', 
                                                            border: '1px solid hsl(var(--muted))', 
                                                            color: 'black', 
                                                            boxShadow: '0px 4px 4px 0px hsl(var(--shadow))' 
                                                        } 
                                                    } 
                                                }}
                                            >
                                                <IconButton onClick={() => handleFileRemove(index)}>
                                                    <IoCloseCircleOutline style={{ fontSize: '20px', color: 'hsl(var(--destructive))' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <div className='w-[100%] h-[100%] flex flex-col justify-center items-center'>
                                {errors && name && errors[name]?.message && <p className='text-destructive flex-grow flex items-center text-[10px]'>No files were Uploaded</p>}
                            </div>
                        )}
                        <div style={{ 
                                        width: '100%', 
                                        marginLeft: '0.2rem', 
                                        marginTop: files?.length === 0 ? '-1rem' : '0.4rem',
                                        display: 'flex', 
                                        flexDirection: 'row', 
                                        justifyContent: 'flex-end', 
                                        textAlign: 'center' 
                                    }}>
                                    <span className='text-[10px] text-[hsl(var(--grey))]'>Maximum Size 5Mb</span>
                                </div>
                    </div>
                                    </div>
                    {errorMessage && (
                        <p className="text-danger text-sm mt-2">{errorMessage}</p>
                    )}
            </div>
        </Fragment>
    );
};

export default MultiFileSelect